/**
 * TableKit Extension
 * 
 * Bundled table extensions with enhanced configuration
 * for Tiptap editor.
 */

import { Extension } from '@tiptap/core';
import { Table, TableOptions } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

// Extended table cell with alignment support
const EnhancedTableCell = TableCell.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            textAlign: {
                default: null,
                parseHTML: element => element.getAttribute('data-text-align'),
                renderHTML: attributes => {
                    if (!attributes.textAlign) {
                        return {};
                    }
                    return { 'data-text-align': attributes.textAlign };
                },
            },
            verticalAlign: {
                default: null,
                parseHTML: element => element.getAttribute('data-vertical-align'),
                renderHTML: attributes => {
                    if (!attributes.verticalAlign) {
                        return {};
                    }
                    return { 'data-vertical-align': attributes.verticalAlign };
                },
            },
            backgroundColor: {
                default: null,
                parseHTML: element => element.style.backgroundColor || null,
                renderHTML: attributes => {
                    if (!attributes.backgroundColor) {
                        return {};
                    }
                    return { style: `background-color: ${attributes.backgroundColor}` };
                },
            },
        };
    },
});

// Extended table header with alignment support
const EnhancedTableHeader = TableHeader.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            textAlign: {
                default: null,
                parseHTML: element => element.getAttribute('data-text-align'),
                renderHTML: attributes => {
                    if (!attributes.textAlign) {
                        return {};
                    }
                    return { 'data-text-align': attributes.textAlign };
                },
            },
            verticalAlign: {
                default: null,
                parseHTML: element => element.getAttribute('data-vertical-align'),
                renderHTML: attributes => {
                    if (!attributes.verticalAlign) {
                        return {};
                    }
                    return { 'data-vertical-align': attributes.verticalAlign };
                },
            },
            backgroundColor: {
                default: null,
                parseHTML: element => element.style.backgroundColor || null,
                renderHTML: attributes => {
                    if (!attributes.backgroundColor) {
                        return {};
                    }
                    return { style: `background-color: ${attributes.backgroundColor}` };
                },
            },
        };
    },
});

export interface TableKitOptions {
    table: Partial<TableOptions> | false;
    tableRow: false | undefined;
    tableCell: false | undefined;
    tableHeader: false | undefined;
}

export const TableKit = Extension.create<TableKitOptions>({
    name: 'tableKit',

    addExtensions() {
        const extensions = [];

        if (this.options.table !== false) {
            extensions.push(
                Table.configure({
                    resizable: true,
                    ...this.options.table,
                })
            );
        }

        if (this.options.tableRow !== false) {
            extensions.push(TableRow);
        }

        if (this.options.tableCell !== false) {
            extensions.push(EnhancedTableCell);
        }

        if (this.options.tableHeader !== false) {
            extensions.push(EnhancedTableHeader);
        }

        return extensions;
    },
});

export { Table, TableRow, EnhancedTableCell as TableCell, EnhancedTableHeader as TableHeader };
export default TableKit;
